import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import CityAutocomplete, { AIRPORT } from "@/components/CityAutocomplete";
import { CalendarBlank, Clock, ArrowRight, ArrowsDownUp } from "@phosphor-icons/react";

export default function BookingWidget() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [pickup, setPickup] = useState(AIRPORT);
  const [dropoff, setDropoff] = useState(null);
  const [tab, setTab] = useState("oneway");
  const [swapKey, setSwapKey] = useState(0);
  const [hours, setHours] = useState(3);
  const [form, setForm] = useState({ date: today, time: "10:00" });
  const [roundTrip, setRoundTrip] = useState(false);
  const [ret, setRet] = useState({ return_date: today, return_time: "18:00" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setR = (k, v) => setRet((r) => ({ ...r, [k]: v }));
  const swap = () => { setPickup(dropoff); setDropoff(pickup); setSwapKey((k) => k + 1); };

  const submit = (e) => {
    e.preventDefault();
    if (!pickup) { toast.error(t("pickup_ph")); return; }
    if (tab === "hourly") {
      navigate("/book", { state: { service_type: "hourly", pickup_id: pickup.id, pickup_name: pickup.name, pickup_lat: pickup.lat, pickup_lon: pickup.lon, dropoff_id: pickup.id, dropoff_name: `${t("hourly_service")} · ${hours}h`, hours, ...form } });
    } else {
      if (!dropoff) { toast.error(t("dropoff_ph")); return; }
      const rt = roundTrip ? { round_trip: true, ...ret } : { round_trip: false };
      navigate("/book", { state: { service_type: "transfer", pickup_id: pickup.id, pickup_name: pickup.name, pickup_lat: pickup.lat, pickup_lon: pickup.lon, dropoff_id: dropoff.id, dropoff_name: dropoff.name, dropoff_lat: dropoff.lat, dropoff_lon: dropoff.lon, ...form, ...rt } });
    }
  };

  const lbl = "font-body text-xs text-[#5A607A] mb-1.5 block";
  const field = "w-full min-w-0 bg-white border border-[#E9E5DE] rounded-2xl pl-10 pr-2 py-3.5 font-body text-sm text-[#0B1533] focus:border-[#EF5A1E] focus:ring-2 focus:ring-[#EF5A1E]/15 focus:outline-none transition-all duration-200";

  return (
    <div className="bg-white rounded-[28px] p-6 sm:p-7 elev-lg" data-testid="booking-widget-card">
      <div className="flex items-center gap-1 bg-[#F5F2ED] rounded-full p-1 mb-6 w-fit">
        {[["oneway", t("tab_oneway")], ["hourly", t("tab_hourly")]].map(([k, label]) => (
          <button key={k} type="button" data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`font-body text-sm px-5 py-2 rounded-full transition-all duration-200 ${tab === k ? "bg-white text-[#0B1533] font-semibold elev-soft" : "text-[#5A607A]"}`}>
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} data-testid="booking-widget" className="space-y-4">
        <div className="relative">
          <label className={lbl}>{t("pickup")}</label>
          {pickup !== undefined && <CityAutocomplete key={`p-${swapKey}`} initial={pickup} accent onSelect={setPickup} placeholder={t("pickup_ph")} testid="pickup-autocomplete" />}
          {tab === "oneway" && (
            <button type="button" data-testid="direction-toggle" onClick={swap} aria-label="Swap"
              className="absolute right-4 top-[38px] z-20 w-9 h-9 rounded-full bg-[#0B1533] text-white flex items-center justify-center hover:bg-[#EF5A1E] transition-colors duration-200 elev-soft">
              <ArrowsDownUp size={16} />
            </button>
          )}
        </div>

        {tab === "oneway" ? (
          <div>
            <label className={lbl}>{t("dropoff")}</label>
            {dropoff !== undefined && <CityAutocomplete key={`d-${swapKey}`} initial={dropoff} onSelect={setDropoff} placeholder={t("dropoff_ph")} testid="dropoff-autocomplete" />}
          </div>
        ) : (
          <div>
            <label className={lbl}>{t("duration")}</label>
            <div className="relative">
              <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA0B0] pointer-events-none" />
              <select data-testid="hours-input" value={hours} onChange={(e) => setHours(Number(e.target.value))} className={field + " appearance-none cursor-pointer"}>
                {Array.from({ length: 23 }, (_, i) => i + 2).map((h) => (
                  <option key={h} value={h}>{h} {t("hours_word")}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="min-w-0">
            <label className={lbl}>{t("date")}</label>
            <div className="relative">
              <CalendarBlank size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA0B0] pointer-events-none" />
              <input type="date" data-testid="date-input" min={today} className={field} value={form.date} onChange={(e) => set("date", e.target.value)} required />
            </div>
          </div>
          <div className="min-w-0">
            <label className={lbl}>{t("time")}</label>
            <div className="relative">
              <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA0B0] pointer-events-none" />
              <input type="time" data-testid="time-input" className={field} value={form.time} onChange={(e) => set("time", e.target.value)} required />
            </div>
          </div>
        </div>

        {tab === "oneway" && (
          <div className="pt-1">
            <label className="flex items-center justify-between cursor-pointer select-none" data-testid="roundtrip-toggle">
              <span className="font-body text-sm text-[#0B1533] font-medium">{t("return_trip")}</span>
              <span className="relative inline-block">
                <input type="checkbox" className="sr-only peer" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} />
                <span className="block w-11 h-6 rounded-full bg-[#E9E5DE] peer-checked:bg-[#EF5A1E] transition-colors duration-200" />
                <span className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200 peer-checked:translate-x-5 elev-soft" />
              </span>
            </label>
            {roundTrip && (
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="min-w-0">
                  <label className={lbl}>{t("return_date")}</label>
                  <div className="relative">
                    <CalendarBlank size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA0B0] pointer-events-none" />
                    <input type="date" data-testid="return-date-input" min={form.date} className={field} value={ret.return_date} onChange={(e) => setR("return_date", e.target.value)} required />
                  </div>
                </div>
                <div className="min-w-0">
                  <label className={lbl}>{t("return_time")}</label>
                  <div className="relative">
                    <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA0B0] pointer-events-none" />
                    <input type="time" data-testid="return-time-input" className={field} value={ret.return_time} onChange={(e) => setR("return_time", e.target.value)} required />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <button type="submit" data-testid="estimate-btn"
          className="group w-full flex items-center justify-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold text-base rounded-full py-4 hover:bg-[#D2450F] transition-all duration-200 elev-soft">
          {t("estimate")}
          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
        </button>
      </form>
    </div>
  );
}
